'use client'

import React, { useState, useEffect } from 'react'
import Link from 'next/link'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Pagination,
  CircularProgress,
  Chip
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import FolderIcon from '@mui/icons-material/Folder'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import classnames from 'classnames'
import dayjs from 'dayjs'
import 'dayjs/locale/id'

import frontCommonStyles from '@views/styles.module.css'
import { fetchArticles, Article, ArticleCategory } from '@/libs/getArticles'

interface ArticlesViewProps {
  initialArticles: Article[]
  initialTotal: number
  categories: ArticleCategory[]
  urlAPI: string
}

export default function ArticlesView({
  initialArticles,
  initialTotal,
  categories,
  urlAPI
}: ArticlesViewProps) {
  const [articles, setArticles] = useState<Article[]>(initialArticles)
  const [total, setTotal] = useState<number>(initialTotal)
  const [loading, setLoading] = useState<boolean>(false)

  // Filter states
  const [search, setSearch] = useState<string>('')
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<number | string>('all')
  const [page, setPage] = useState<number>(1)
  const itemsPerPage = 6

  // Handle search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1) // Reset to page 1 on new search
    }, 500)
    return () => clearTimeout(timer)
  }, [search])

  // Fetch data on filter change
  useEffect(() => {
    let active = true

    // Skip first load since we have initialArticles
    if (search === '' && selectedCategory === 'all' && page === 1 && articles === initialArticles) {
      return
    }

    const loadFilteredArticles = async () => {
      setLoading(true)
      const catId = selectedCategory === 'all' ? null : Number(selectedCategory)
      const res = await fetchArticles(urlAPI, {
        nama: debouncedSearch,
        id_kategori: catId,
        halaman: page - 1,
        baris: itemsPerPage
      })

      if (active) {
        setArticles(res.list)
        setTotal(res.jumlahrecord)
        setLoading(false)
      }
    }

    loadFilteredArticles()

    return () => {
      active = false
    }
  }, [debouncedSearch, selectedCategory, page])

  const getArticleImageUrl = (gambarJson?: string) => {
    if (!gambarJson) return '/images/icon/NoData.png'
    try {
      const images = JSON.parse(gambarJson)
      if (Array.isArray(images) && images.length > 0) {
        const filename = images[0]
        const baseRegisUrl = process.env.NEXT_PUBLIC_URL_IMAGE_REGIS || ''
        if (baseRegisUrl.endsWith('rumahduka/')) {
          return baseRegisUrl.slice(0, -10) + 'artikel/' + filename
        }
        return baseRegisUrl + 'artikel/' + filename
      }
    } catch (e) {
      // Not a JSON or empty array
    }
    return '/images/icon/NoData.png'
  }

  const handleCategoryChange = (event: React.SyntheticEvent, newValue: string | number) => {
    setSelectedCategory(newValue)
    setPage(1)
  }

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
    // Scroll smoothly to top of article section
    const element = document.getElementById('articles-header')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const pageCount = Math.ceil(total / itemsPerPage)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Header Info */}
      <Box id="articles-header" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            fontSize: { xs: '2.25rem', md: '3rem' }
          }}
        >
          Artikel & Panduan Kedukaan
        </Typography>
        <Typography color="text.secondary" sx={{ lineHeight: 1.8, maxWidth: 800 }}>
          Kumpulan artikel panduan rumah duka, alur kedukaan, pengurusan administrasi jenazah, kremasi, dan checklist keluarga untuk membantu Anda di masa-masa sulit.
        </Typography>
      </Box>

      {/* Filter and Search Controls */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
          p: 2,
          borderRadius: 3,
          backgroundColor: 'background.paper',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        <Tabs
          value={selectedCategory}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            '& .MuiTabs-indicator': {
              display: 'none'
            },
            '& .MuiTab-root': {
              minHeight: 40,
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.9rem',
              mr: 1,
              px: 3,
              color: 'text.secondary',
              transition: 'all 0.3s ease',
              '&.Mui-selected': {
                color: 'white',
                backgroundColor: 'primary.main',
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.25)'
              },
              '&:hover:not(.Mui-selected)': {
                backgroundColor: 'action.hover'
              }
            }
          }}
        >
          <Tab value="all" label="Semua Kategori" />
          {categories.map((cat) => (
            <Tab key={cat.ID_KATEGORI} value={cat.ID_KATEGORI} label={cat.KATEGORI} />
          ))}
        </Tabs>

        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari artikel..."
          variant="outlined"
          size="small"
          sx={{
            minWidth: { xs: '100%', md: 280 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2.5,
              backgroundColor: 'grey.50'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {/* Articles Gallery */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : articles.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            px: 2,
            borderRadius: 3,
            backgroundColor: 'grey.50',
            border: '2px dashed rgba(0,0,0,0.1)'
          }}
        >
          <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 700, mb: 1 }}>
            Tidak Ada Artikel Ditemukan
          </Typography>
          <Typography color="text.secondary">
            Coba gunakan kata kunci pencarian lain atau pilih kategori yang berbeda.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3.5}>
          {articles.map((article) => {
            const dateStr = article.PUBLISHED_AT || article.CREATED_AT
            const formattedDate = dateStr ? dayjs(dateStr).locale('id').format('DD MMMM YYYY') : '-'
            let categorySlug = article.SLUG_KATEGORI
            if (!categorySlug && article.ID_KATEGORI) {
              const matchedCat = categories.find(c => c.ID_KATEGORI === article.ID_KATEGORI)
              if (matchedCat) {
                categorySlug = matchedCat.SLUG
              }
            }
            if (!categorySlug) {
              categorySlug = 'uncategorized'
            }
            const articleUrl = `/${categorySlug}/${article.SLUG}`

            return (
              <Grid item xs={12} sm={6} md={4} key={article.ID_ARTIKEL}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    overflow: 'hidden',
                    borderColor: 'divider',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.03)',
                    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 28px rgba(0, 0, 0, 0.08)',
                      borderColor: 'primary.light',
                      '& .read-more-btn': {
                        color: 'primary.main',
                        '& svg': {
                          transform: 'translateX(4px)'
                        }
                      }
                    }
                  }}
                >
                  {/* Article Thumbnail */}
                  <Box
                    component={Link}
                    href={articleUrl}
                    sx={{
                      display: 'block',
                      width: '100%',
                      pt: '56.25%', // 16:9 aspect ratio
                      position: 'relative',
                      backgroundColor: 'grey.100',
                      overflow: 'hidden'
                    }}
                  >
                    <img
                      src={getArticleImageUrl(article.GAMBAR)}
                      alt={article.JUDUL}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>

                  {/* Card content */}
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                      {article.NAMA_KATEGORI && (
                        <Chip
                          icon={<FolderIcon style={{ fontSize: 13 }} />}
                          label={article.NAMA_KATEGORI}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{
                            height: 22,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            borderRadius: 1.5
                          }}
                        />
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                        <CalendarTodayIcon style={{ fontSize: 13 }} />
                        <Typography variant="caption" sx={{ fontWeight: 500 }}>
                          {formattedDate}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="h6"
                      component={Link}
                      href={articleUrl}
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        textDecoration: 'none',
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        '&:hover': {
                          color: 'primary.main'
                        }
                      }}
                    >
                      {article.JUDUL}
                    </Typography>

                    <Typography
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.7,
                        fontSize: '0.9rem',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {article.EXERPT}
                    </Typography>
                  </CardContent>

                  {/* Actions */}
                  <CardActions sx={{ px: 3, pb: 3, pt: 0, mt: 'auto' }}>
                    <Button
                      component={Link}
                      href={articleUrl}
                      className="read-more-btn"
                      variant="text"
                      color="inherit"
                      sx={{
                        p: 0,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        '& svg': {
                          fontSize: 18,
                          ml: 0.5,
                          transition: 'transform 0.2s ease'
                        }
                      }}
                    >
                      Baca Selengkapnya <ArrowForwardIcon />
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      )}

      {/* Pagination Controls */}
      {!loading && pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={handlePageChange}
            color="primary"
            variant="outlined"
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2,
                fontWeight: 600
              }
            }}
          />
        </Box>
      )}
    </Box>
  )
}
